import type { CashflowTarget } from '@moment/horizon-data-access/proposal'
import type {
  CashflowBandConstraint,
  ModelConstraint,
} from '@/types/api/constraints'

import { CashflowBandConstraintType } from '@moment/horizon-data-access/proposal'

import { generateId } from '@/portfolio/proposal-builder/data/helpers/sleeve-manager'
import { Priority } from '@/portfolio/proposal-builder/types'
import { CashflowFrequency } from '@/portfolio/proposals/cashflow-target/types'

function isCashflowBandConstraint(
  constraint: ModelConstraint,
): constraint is CashflowBandConstraint {
  return constraint.type === CashflowBandConstraintType.cashflow_band
}

export function readCashflowBand(
  constraints: ModelConstraint[],
): CashflowBandConstraint | undefined {
  return constraints.find(isCashflowBandConstraint)
}

// The backend only levels recurring targets; one-time targets are ignored.
export function hasRecurringCashflowTarget(targets: CashflowTarget[]): boolean {
  return targets.some(
    (target) => target.frequency !== CashflowFrequency.OneTime,
  )
}

/** Upserts the sleeve's single band rule; `null` removes it. */
export function applyCashflowBand(
  constraints: ModelConstraint[],
  maxDeviationRatio: number | null,
): ModelConstraint[] {
  const existing = readCashflowBand(constraints)
  if (maxDeviationRatio === null) {
    return constraints.filter(
      (constraint) => !isCashflowBandConstraint(constraint),
    )
  }
  if (!existing) {
    const created: CashflowBandConstraint = {
      id: generateId(),
      type: CashflowBandConstraintType.cashflow_band,
      max_deviation_ratio: maxDeviationRatio,
      constraint_priority: Priority.Low,
    }
    return [...constraints, created]
  }
  return constraints.map((constraint) =>
    constraint === existing
      ? { ...existing, max_deviation_ratio: maxDeviationRatio }
      : constraint,
  )
}

/** Sets the sleeve band's priority; without a band there is nothing to set. */
export function applyCashflowBandPriority(
  constraints: ModelConstraint[],
  priority: Priority,
): ModelConstraint[] {
  const existing = readCashflowBand(constraints)
  if (!existing) return constraints
  return constraints.map((constraint) =>
    constraint === existing
      ? { ...existing, constraint_priority: priority }
      : constraint,
  )
}
